/**
 * 後台 — 首頁 Dashboard
 *
 * KPI 卡(今日/本月/待出貨/活躍) + 顧問待跟進清單(4 區) + 近期動態
 * 從 GET /api/admin/dashboard 一次拉完整 payload,避免多次 round-trip
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminExportSleepingMembers,
  adminListSleepingMembers,
  getAdminDashboard,
  ORDER_STATUS_LABELS,
  type DashboardData,
  type DashboardProductRank,
  type DashboardTrendPoint,
  type SleepingList,
} from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import DataTable, { Column } from "../../components/DataTable";
import TierBadge, { TIER_LABEL, type Tier } from "../../components/TierBadge";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpeCompact } from "../../utils/date";


export default function Dashboard() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState<number>(30);
  const [sleepingOpen, setSleepingOpen] = useState(false);   // #17 久未互動全名單 modal

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const r = await getAdminDashboard(rangeDays);
        setData(r);
      } catch {
        useUIStore.getState().showToast("載入失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [role, rangeDays, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <AdminHeader active="dashboard" />
        <div className="p-10 text-center text-muted">載入中…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-cream">
        <AdminHeader active="dashboard" />
        <div className="p-10 text-center text-muted">無資料</div>
      </div>
    );
  }

  const k = data.kpis;

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="dashboard" />

      <div className="p-6 space-y-6">
        {/* Range selector + KPI 列 */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted">最近區間</div>
          <div className="flex gap-1 bg-white rounded-lg p-1 border border-line">
            {[7, 30, 90].map((r) => (
              <button
                key={r}
                onClick={() => setRangeDays(r)}
                className={`text-xs px-3 py-1 rounded transition ${
                  rangeDays === r ? "bg-primary text-white" : "text-muted hover:bg-cream"
                }`}
              >
                {r} 天
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="今日新訂單"
            primary={`NT$ ${k.today_orders_amount.toLocaleString()}`}
            sub={`${k.today_orders_count} 筆`}
          />
          <KpiCard
            label="本月營收"
            primary={`NT$ ${k.month_revenue.toLocaleString()}`}
          />
          <KpiCard
            label="待出貨訂單"
            primary={String(k.pending_orders_count)}
            sub="筆"
            highlight={k.pending_orders_count > 0}
            onClick={() => navigate("/admin/orders?status=pending_payment_mock")}
          />
          <KpiCard
            label="7 日活躍會員"
            primary={String(k.active_members_7d)}
            sub="人"
          />
        </div>

        {/* 區間趨勢 — 訂單數/營收 sparkline + 區間摘要 */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="card lg:col-span-2">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-sm font-bold text-ink">最近 {rangeDays} 天營收趨勢</div>
              <div className="text-xs text-muted">
                訂單 {data.trends.total_count} 筆 · 平均客單 NT$ {data.trends.avg_order_value.toLocaleString()}
              </div>
            </div>
            <RevenueSparkline series={data.trends.series} />
          </div>

          <div className="card">
            <div className="text-sm font-bold text-ink mb-3">會員等級分佈</div>
            <TierDistribution counts={data.tier_distribution.counts} total={data.tier_distribution.total} />
          </div>
        </div>

        {/* 商品銷售排行 */}
        <div className="card">
          <div className="text-sm font-bold text-ink mb-3">
            最近 {rangeDays} 天熱銷商品 Top {data.product_ranking.length}
          </div>
          {data.product_ranking.length === 0 ? (
            <div className="text-muted text-sm py-4 text-center">此區間無訂單</div>
          ) : (
            <ProductRanking items={data.product_ranking} />
          )}
        </div>

        {/* 待跟進清單 — 2x2 grid */}
        <div className="grid lg:grid-cols-2 gap-5">
          <TodoCard
            title="待出貨訂單"
            empty="目前無待出貨訂單"
            items={data.todos.pending_orders}
            viewAllPath="/admin/orders"
            renderItem={(o) => (
              <button
                key={o.order_id}
                onClick={() => navigate(`/admin/orders/${o.order_id}`)}
                className="w-full text-left flex items-center justify-between py-2 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded transition"
              >
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">
                    {o.name || "—"}
                    <span className="font-mono text-xs text-muted ml-2">#{(o.order_id || "").slice(0, 8)}</span>
                  </div>
                  <div className="text-xs text-muted mt-0.5">{fmtTime(o.created_at)}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-sm font-bold text-ink">NT$ {o.total.toLocaleString()}</div>
                  <div className="text-xxs text-muted">{ORDER_STATUS_LABELS[o.status] || o.status}</div>
                </div>
              </button>
            )}
          />

          <TodoCard
            title="近期生日關懷"
            hint="未來 14 天內"
            empty="近期無會員生日"
            items={data.todos.birthday_upcoming}
            renderItem={(it) => (
              <button
                key={it.line_user_id}
                onClick={() => navigate(`/admin/members/${it.line_user_id}`)}
                className="w-full text-left flex items-center justify-between py-2 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded transition"
              >
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{it.name || "—"}</div>
                  <div className="text-xs text-muted mt-0.5">{it.birthday} · {tierShort(it.tier)}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className={`text-sm font-bold ${it.days_until <= 3 ? "text-coral" : "text-primary"}`}>
                    {it.days_until === 0 ? "今天" : `${it.days_until} 天後`}
                  </div>
                </div>
              </button>
            )}
          />

          <TodoCard
            title="會籍即將到期"
            hint="未來 30 天內"
            empty="近期無會籍到期"
            items={data.todos.membership_expiring}
            renderItem={(it) => (
              <button
                key={it.line_user_id}
                onClick={() => navigate(`/admin/members/${it.line_user_id}`)}
                className="w-full text-left flex items-center justify-between py-2 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded transition"
              >
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate flex items-center gap-2">
                    {it.name || "—"}
                    <TierBadge tier={(it.tier as Tier) || "general"} size="sm" />
                  </div>
                  <div className="text-xs text-muted mt-0.5">{fmtDate(it.membership_end)}</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className={`text-sm font-bold ${it.days_left <= 7 ? "text-coral" : "text-ink"}`}>
                    {it.days_left} 天
                  </div>
                </div>
              </button>
            )}
          />

          <TodoCard
            title="久未互動會員"
            hint={`${data.todos.sleeping_days} 天以上未互動${data.todos.sleeping_exclude_general ? ",排除霽雲薈" : ""}(門檻在系統設定調整)`}
            empty={data.todos.sleeping_exclude_general ? "所有付費會員近期均有互動" : "所有會員近期均有互動"}
            items={data.todos.sleeping_members}
            count={data.todos.sleeping_total}
            onViewAll={() => setSleepingOpen(true)}
            renderItem={(it) => (
              <button
                key={it.line_user_id}
                onClick={() => navigate(`/admin/members/${it.line_user_id}`)}
                className="w-full text-left flex items-center justify-between py-2 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded transition"
              >
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate flex items-center gap-2">
                    {it.name || "—"}
                    <TierBadge tier={(it.tier as Tier) || "general"} size="sm" />
                  </div>
                  <div className="text-xs text-muted mt-0.5">
                    最後互動 {it.last_interaction_at ? fmtDate(it.last_interaction_at) : "—"}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-sm font-bold text-coral">
                    {it.days_silent === null ? "從未互動" : `${it.days_silent} 天`}
                  </div>
                </div>
              </button>
            )}
          />
        </div>

        {sleepingOpen && (
          <SleepingListModal
            initialDays={data.todos.sleeping_days}
            initialExclude={data.todos.sleeping_exclude_general}
            onClose={() => setSleepingOpen(false)}
          />
        )}

        {/* 近期動態 */}
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="最近 5 筆訂單" viewAllPath="/admin/orders" onLinkClick={() => navigate("/admin/orders")}>
            {data.recent.recent_orders.length === 0 ? (
              <div className="text-muted text-sm py-6 text-center">尚無訂單</div>
            ) : (
              data.recent.recent_orders.map((o) => (
                <button
                  key={o.order_id}
                  onClick={() => navigate(`/admin/orders/${o.order_id}`)}
                  className="w-full text-left flex items-center justify-between py-2 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded transition"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">
                      {o.name || "—"}
                      <span className="font-mono text-xs text-muted ml-2">#{(o.order_id || "").slice(0, 8)}</span>
                    </div>
                    <div className="text-xs text-muted mt-0.5">{fmtTime(o.created_at)}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-sm font-bold">NT$ {o.total.toLocaleString()}</div>
                    <div className="text-xxs text-muted">{ORDER_STATUS_LABELS[o.status] || o.status}</div>
                  </div>
                </button>
              ))
            )}
          </Card>

          <Card title="最近 10 筆互動">
            {data.recent.recent_interactions.length === 0 ? (
              <div className="text-muted text-sm py-6 text-center">尚無互動</div>
            ) : (
              data.recent.recent_interactions.map((it, i) => (
                <button
                  key={i}
                  onClick={() => it.member_uid && navigate(`/admin/members/${it.member_uid}`)}
                  className="w-full text-left py-2 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded transition"
                >
                  <div className="text-sm text-ink">
                    <span className="text-muted text-xs mr-2">{it.type}</span>
                    {(it.content || "").slice(0, 40) || "—"}
                  </div>
                  <div className="text-xs text-muted mt-0.5">{fmtTime(it.timestamp)}</div>
                </button>
              ))
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}


// ── 子元件 ──────────────────────────────────

function KpiCard({
  label,
  primary,
  sub,
  highlight,
  onClick,
}: {
  label: string;
  primary: string;
  sub?: string;
  highlight?: boolean;
  onClick?: () => void;
}) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`card text-left ${onClick ? "hover:shadow-md transition cursor-pointer" : ""} ${highlight ? "ring-2 ring-coral/40" : ""}`}
    >
      <div className="text-xs text-muted">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className={`text-2xl font-bold ${highlight ? "text-coral" : "text-ink"}`}>{primary}</span>
        {sub && <span className="text-xs text-muted">{sub}</span>}
      </div>
    </Tag>
  );
}

function TodoCard<T>({
  title,
  hint,
  empty,
  items,
  viewAllPath,
  count,
  onViewAll,
  renderItem,
}: {
  title: string;
  hint?: string;
  empty: string;
  items: T[];
  viewAllPath?: string;
  /** 卡片只放前幾筆時,標題括號裡顯示的總數(不帶 = items.length) */
  count?: number;
  /** 「查看全部」不是換頁而是開 modal 時用這個;跟 viewAllPath 擇一 */
  onViewAll?: () => void;
  renderItem: (item: T) => JSX.Element;
}) {
  const navigate = useNavigate();
  const link = onViewAll || (viewAllPath ? () => navigate(viewAllPath) : undefined);
  return (
    <Card title={title} hint={hint} count={count ?? items.length} onLinkClick={link}>
      {items.length === 0 ? (
        <div className="text-muted text-sm py-6 text-center">{empty}</div>
      ) : (
        <div>{items.map(renderItem)}</div>
      )}
    </Card>
  );
}

function Card({
  title,
  hint,
  count,
  viewAllPath,
  onLinkClick,
  children,
}: {
  title: string;
  hint?: string;
  count?: number;
  /** 保留給舊呼叫端;有 onLinkClick 就會顯示「查看全部」 */
  viewAllPath?: string;
  onLinkClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-sm font-bold text-ink">
            {title}
            {typeof count === "number" && count > 0 && (
              <span className="ml-2 text-xs font-normal text-muted">({count})</span>
            )}
          </div>
          {hint && <div className="text-xxs text-muted mt-0.5">{hint}</div>}
        </div>
        {(viewAllPath || onLinkClick) && (
          <button onClick={onLinkClick} className="text-xs text-primary hover:underline">
            查看全部 →
          </button>
        )}
      </div>
      {children}
    </div>
  );
}


// ── #17 久未互動全名單 modal(2026-10-06):可臨時改門檻、匯出 Excel ──

function SleepingListModal({
  initialDays,
  initialExclude,
  onClose,
}: {
  initialDays: number;
  initialExclude: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [days, setDays] = useState(String(initialDays));
  const [exclude, setExclude] = useState(initialExclude);
  const [list, setList] = useState<SleepingList | null>(null);
  const [loading, setLoading] = useState(false);

  const effectiveDays = Math.min(365, Math.max(1, parseInt(days, 10) || initialDays));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminListSleepingMembers({ days: effectiveDays, exclude_general: exclude })
      .then((r) => { if (!cancelled) setList(r); })
      .catch(() => useUIStore.getState().showToast("載入名單失敗", "error"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [effectiveDays, exclude]);

  async function exportXlsx() {
    try {
      const blob = await adminExportSleepingMembers({ days: effectiveDays, exclude_general: exclude });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `久未互動會員_${effectiveDays}天_${todayTpeCompact()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      useUIStore.getState().showToast("匯出失敗", "error");
    }
  }

  const columns: Column<SleepingList["members"][number]>[] = [
    {
      key: "name",
      header: "姓名",
      render: (r) => (
        <span>
          {r.name || "—"}
          {r.display_name && r.display_name !== r.name && (
            <span className="ml-1 text-xs text-muted">{r.display_name}</span>
          )}
        </span>
      ),
    },
    { key: "phone", header: "手機", render: (r) => r.phone || "—" },
    { key: "tier", header: "等級", render: (r) => <TierBadge tier={(r.tier as Tier) || "general"} size="sm" /> },
    { key: "last", header: "最後互動", render: (r) => fmtDate(r.last_interaction_at) },
    {
      key: "silent",
      header: "未互動",
      render: (r) => (
        <span className="text-coral font-medium whitespace-nowrap">{r.days_silent === null ? "從未互動" : `${r.days_silent} 天`}</span>
      ),
    },
    { key: "end", header: "會籍迄", render: (r) => fmtDate(r.membership_end) },
    { key: "spent", header: "累計消費", render: (r) => `NT$ ${(r.total_spent || 0).toLocaleString()}` },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-line flex flex-wrap items-end gap-3">
          <div className="mr-auto">
            <div className="text-lg font-bold text-ink">久未互動會員{list ? `(${list.total})` : ""}</div>
            <div className="text-xxs text-muted mt-0.5">
              這裡改門檻只影響這份名單;要改首頁預設請到系統設定。
            </div>
          </div>
          <div>
            <label className="label">超過幾天</label>
            <input className="input w-24" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-ink pb-2">
            <input type="checkbox" checked={exclude} onChange={(e) => setExclude(e.target.checked)} />
            排除霽雲薈
          </label>
          <button onClick={exportXlsx} className="btn-secondary text-sm" disabled={!list || list.total === 0}>
            匯出 Excel
          </button>
          <button onClick={onClose} className="text-sm text-muted px-2 py-1.5 hover:text-ink">關閉</button>
        </div>
        <div className="overflow-auto p-4">
          {list && (
            <DataTable
              columns={columns}
              rows={list.members}
              onRowClick={(r) => navigate(`/admin/members/${r.line_user_id}`)}
              emptyHint={loading ? "載入中…" : "此門檻下沒有久未互動的會員"}
            />
          )}
        </div>
      </div>
    </div>
  );
}


// ── helpers ─────────────────────────────────

function tierShort(tier: string): string {
  return TIER_LABEL[tier as Tier] || tier;
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return String(iso).slice(0, 16).replace("T", " ");
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}


// ── B4 新元件:Sparkline / TierDistribution / ProductRanking ──

function RevenueSparkline({ series }: { series: DashboardTrendPoint[] }) {
  // SVG 折線圖 — 不引入 chart library,保持 bundle 小
  if (!series || series.length === 0) {
    return <div className="text-muted text-sm py-6 text-center">尚無資料</div>;
  }
  const w = 600;
  const h = 80;
  const padX = 8;
  const padY = 8;
  const maxRev = Math.max(...series.map((s) => s.revenue), 1);
  const dx = (w - padX * 2) / Math.max(series.length - 1, 1);
  const points = series.map((s, i) => {
    const x = padX + i * dx;
    const y = padY + (h - padY * 2) * (1 - s.revenue / maxRev);
    return { x, y, ...s };
  });
  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const areaD = pathD + ` L ${points[points.length - 1].x} ${h - padY} L ${points[0].x} ${h - padY} Z`;

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none">
        <path d={areaD} fill="#005844" opacity={0.12} />
        <path d={pathD} fill="none" stroke="#005844" strokeWidth={2} />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={2.5}
            fill="#005844"
            opacity={p.revenue > 0 ? 1 : 0.3}
          >
            <title>{`${p.date}: NT$ ${p.revenue.toLocaleString()} · ${p.count} 筆`}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-xxs text-muted mt-1">
        <span>{series[0]?.date.slice(5)}</span>
        <span>{series[series.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}


function TierDistribution({ counts, total }: { counts: Record<string, number>; total: number }) {
  if (total === 0) {
    return <div className="text-muted text-sm py-6 text-center">尚無會員</div>;
  }
  const tiers: { key: string; label: string; color: string }[] = [
    { key: "timeless_club", label: "恆時薈", color: "bg-[#1D252D]" },
    { key: "health_affiliate", label: "潤雲薈", color: "bg-coral" },
    { key: "general", label: "霽雲薈", color: "bg-[#C7D8CD]" },
  ];
  return (
    <div className="space-y-2">
      {tiers.map((t) => {
        const c = counts[t.key] || 0;
        const pct = total > 0 ? (c / total) * 100 : 0;
        return (
          <div key={t.key}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="text-ink">{t.label}</span>
              <span className="text-muted">
                {c} 人 · {pct.toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-cream rounded overflow-hidden">
              <div
                className={`h-full ${t.color}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
      <div className="text-xxs text-muted text-right mt-2">共 {total} 位 active 會員</div>
    </div>
  );
}


function ProductRanking({ items }: { items: DashboardProductRank[] }) {
  const maxRev = Math.max(...items.map((i) => i.revenue), 1);
  return (
    <div className="space-y-2">
      {items.map((p, idx) => {
        const pct = (p.revenue / maxRev) * 100;
        return (
          <div key={p.product_id} className="space-y-1">
            <div className="flex items-baseline justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="text-xs text-muted font-mono w-5">#{idx + 1}</span>
                <span className="text-ink">{p.name}</span>
              </span>
              <span className="text-muted text-xs">
                {p.qty} 件 · NT$ {p.revenue.toLocaleString()}
              </span>
            </div>
            <div className="h-1.5 bg-cream rounded overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

